<template>
    <div id="batch-page">
        <div class="batch-actions">
            <div class="top-row">
                <ui5-button @click="processBatch">
                    {{ trans('pages.batch_page.process_list') }}
                </ui5-button>

                <div>
                    <ui5-label>
                        {{ trans('pages.batch_page.new_status_for_products') }}
                    </ui5-label>
                    <ui5-select
                        @change="
                            selectedStatus = $event.detail.selectedOption.value
                        "
                    >
                        <ui5-option :value="null">
                            {{ trans('pages.batch_page.select_status') }}
                        </ui5-option>
                        <template v-for="status in sortedStatusList">
                            <ui5-option
                                :value="status.code"
                                :selected="status.code == selectedStatus"
                                :key="status.code"
                            >
                                {{ status.code }} - {{ status.external_name }}
                            </ui5-option>
                        </template>
                    </ui5-select>
                </div>
            </div>

            <div class="product-nr">
                <ui5-label for="productnr">
                    {{ trans('pages.batch_page.enter_product_number') }}
                </ui5-label>
                <div>
                    <ui5-input
                        type="Text"
                        name="productnr"
                        :value="productNrInput"
                        @keyup.enter="
                            addProductToBatch(productNrInput);
                            productNrInput = '';
                        "
                        @input="productNrInput = $event.target.value"
                    />

                    <ui5-button
                        @click="
                            addProductToBatch(productNrInput);
                            productNrInput = '';
                        "
                    >
                        {{ trans('pages.batch_page.add') }}
                    </ui5-button>
                    <ui5-button @click="onScanProductClick">
                        <i class="fa fa-camera"></i>
                    </ui5-button>
                </div>
            </div>

            <div
                class="product-nr"
                v-if="hasRight('productionmanagement')"
            >
                <ui5-label for="paintworkformnr">
                    {{
                        trans(
                            'pages.batch_page.enter_lakbon_or_paintwork_order_number'
                        )
                    }}
                </ui5-label>
                <div>
                    <ui5-input
                        type="Text"
                        name="paintworkformnr"
                        :value="paintworkformNrInput"
                        :disabled="paintworkformLoading"
                        @keyup.enter="
                            addPaintworkformOrLakbonToBatch(
                                paintworkformNrInput
                            );
                            paintworkformNrInput = '';
                        "
                        @input="paintworkformNrInput = $event.target.value"
                    />

                    <ui5-button
                        :disabled="paintworkformLoading"
                        @click="
                            addPaintworkformOrLakbonToBatch(
                                paintworkformNrInput
                            );
                            paintworkformNrInput = '';
                        "
                    >
                        <spinner
                            v-if="paintworkformLoading"
                            style="display: inline-block; margin-right: 8px"
                        />
                        {{ trans('pages.batch_page.add') }}
                    </ui5-button>
                    <ui5-button @click="onScanPaintworkformClick">
                        <i class="fa fa-camera"></i>
                    </ui5-button>
                </div>
            </div>
        </div>
        <ui5-list
            mode="Delete"
            @item-delete="itemDelete"
        >
            <template v-for="(product, productIndex) in products">
                <ui5-li :key="product.ID">
                    {{ product.productNr }} - {{ product.name }} -
                    {{ product.reference || '' }}
                </ui5-li>
            </template>
        </ui5-list>
    </div>
</template>
<script>
import statusApi from '../api/status';
import productApi from '../api/product';
import { mapGetters } from 'vuex';
import { showInfoMessage, showErrorMessage } from '../dialogs';
import { productMocker, paintWorkFormMocker } from '../shared/qrMockers';
import { LAKBON_PATTERN, LAKWERKORDER_PATTERN } from '../shared/paintworkPatterns';
import localization from '../mixins/localization';
import Spinner from '../components/loader/Spinner';

export default {
    components: { Spinner },
    mixins: [localization],
    data() {
        return {
            statusList: null,
            productNrInput: '',
            paintworkformNrInput: '',
            paintworkformLoading: false,
            products: [],
            selectedStatus: null,
        };
    },
    computed: {
        ...mapGetters({
            hasRight: 'currentUser/hasRight',
        }),
        sortedStatusList() {
            if (!this.statusList) return [];
            return this.statusList.sort((a, b) =>
                a.external_name.localeCompare(b.external_name)
            );
        },
    },
    methods: {
        itemDelete(event) {
            const item = event.detail.item;
            const index = Array.prototype.indexOf.call(
                item.parentNode.children,
                item
            );
            this.$delete(this.products, index);
        },
        onScanProductClick() {
            this.$bus.$emit('qr_scanner.start', 'batch', {
                singleScan: false,
                mocker: productMocker,
            });
        },
        onScanPaintworkformClick() {
            this.$bus.$emit('qr_scanner.start', 'batch', {
                singleScan: false,
                mocker: paintWorkFormMocker,
            });
        },
        onScan(value) {
            var decoded = null;
            try {
                decoded = JSON.parse(value);
            } catch (error) {
                console.error('Error parsing QR code', error);
            }

            if (decoded && decoded.context === 'product') {
                this.addProductToBatch(decoded.productId);
            } else if (decoded && decoded.context === 'paintworkform') {
                this.addPaintworkformOrLakbonToBatch(decoded.paintworkformId);
            } else {
                showErrorMessage(
                    this.trans('pages.batch_page.invalid_qr_code'),
                    null,
                    1500
                );
            }
        },
        processBatch() {
            if (!this.selectedStatus) {
                return showErrorMessage(
                    this.trans('pages.batch_page.must_select_status')
                );
            }
            if (!this.products || !this.products.length) {
                return showErrorMessage(
                    this.trans('pages.batch_page.must_select_product')
                );
            }

            const products = [];
            this.products.forEach((product) => {
                statusApi
                    .updateProductStatus(product.ID, this.selectedStatus)
                    .catch(() => {
                        showErrorMessage(
                            this.trans('pages.batch_page.unknown_error_for', {
                                productNr: product.productNr,
                            })
                        );
                        products.push(product);
                    });
            });

            if (products.length == 0) {
                showInfoMessage(
                    this.trans('pages.batch_page.all_products_updated'),
                    () => {
                        this.$router.push('/menu');
                    }
                );
            } else {
                this.products = products;
            }
        },
        populateStateList() {
            statusApi.fetchAllStatuses().then((response) => {
                this.statusList = response.data.data;
            });
        },
        addPaintworkformOrLakbonToBatch(input, callback) {
            if (!input || input.trim() === '') {
                return;
            }

            const trimmedInput = input.trim();

            // Dictionary mapping regex patterns to API functions and error messages
            const patterns = {
                lakbon: {
                    regex: LAKBON_PATTERN,
                    apiFn: () => productApi.fetchProductsBatchByLakbon(trimmedInput),
                    errorKey: 'pages.batch_page.invalid_lakbon_number',
                },
                lakwerkorder: {
                    regex: LAKWERKORDER_PATTERN,
                    apiFn: () => productApi.fetchProductsBatchByPaintworkform(trimmedInput),
                    errorKey: 'pages.batch_page.invalid_paintwork_order_number',
                },
            };

            // Find the matching pattern
            const matchedPattern = Object.values(patterns).find((p) =>
                p.regex.test(trimmedInput)
            );

            if (matchedPattern) {
                this.paintworkformLoading = true;
                matchedPattern
                    .apiFn()
                    .then((response) => {
                        response.data.data.forEach((product) => {
                            this.addProduct(product);
                        });
                    })
                    .catch((error) => {
                        if (error.response && error.response.status === 404) {
                            showErrorMessage(
                                this.trans(matchedPattern.errorKey),
                                callback
                            );
                        } else {
                            showErrorMessage(
                                this.trans('pages.batch_page.unknown_error')
                            );
                        }
                    })
                    .finally(() => {
                        this.paintworkformLoading = false;
                    });
            } else {
                // Invalid format
                showErrorMessage(
                    this.trans('pages.batch_page.invalid_number_format'),
                    callback
                );
            }
        },
        addProduct(product) {
            if (this.products.some((x) => x.productNr == product.productNr)) {
                showInfoMessage(
                    this.trans('pages.batch_page.product_already_in_list', {
                        productNr: product.productNr,
                    })
                );
            } else {
                this.products.unshift(product);
                showInfoMessage(
                    this.trans('pages.batch_page.product_added_to_list', {
                        productNr: product.productNr,
                    }),
                    null,
                    1500
                );
            }
        },
        addProductToBatch(productNr, callback) {
            productApi
                .fetchProductBatch(productNr)
                .then((response) => {
                    const product = response.data.data;
                    this.addProduct(product);
                })
                .catch((error) => {
                    if (error.response && error.response.status === 404) {
                        showErrorMessage(
                            this.trans(
                                'pages.batch_page.invalid_product_number'
                            ),
                            callback
                        );
                    } else {
                        showErrorMessage(
                            this.trans('pages.batch_page.unknown_error')
                        );
                    }
                });
        },
    },
    mounted() {
        this.populateStateList();

        this.$bus.$on('barcode-scan', (data) => {
            this.addProductToBatch(data);
        });

        this.$bus.$on('qr_scanner.on_scan', (key, value) => {
            if (key === 'batch') {
                this.onScan(value);
            }
        });
    },
};
</script>
